import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { CommonModalProps } from '@wajeez-web/components/modals';
import { Server } from '@wajeez-web/core/api-base';
import {
  useCreatePublisher,
  useEditPublisher,
  useGetPublisherById,
} from '@wajeez-web/components/helpers';
import { FormTextInput } from '@wajeez-web/components/form-builder';
import {
  clearPublisherById,
  createPublisher,
  editPublisher,
  getPublisherById,
} from '@wajeez-web/stores/content';
import { useForm, SubmitHandler } from 'react-hook-form';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';

export interface PublisherModalProps extends CommonModalProps<number> {
  id?: number;
  isLoadingExternal?: boolean;
}

type Input = {
  name: string;
};

const PublisherModal = ({
  isOpen,
  onClose,
  onSubmit,
  id,
  isLoadingExternal,
}: PublisherModalProps) => {
  const { status, data } = useGetPublisherById();

  useEffect(() => {
    if (id) {
      getPublisherById(id);
    }

    return () => {
      clearPublisherById();
    };
  }, [id]);

  return (
    <Modal
      title={'Publisher'}
      open={isOpen}
      onClose={onClose}
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
      handleAlert
    >
      {status === 'loading' ? (
        <div className="h-[400px] flex justify-center items-center">
          <Spin />
        </div>
      ) : !id ? (
        <PublisherForm
          onSubmit={onSubmit}
          onClose={onClose}
          isLoadingExternal={isLoadingExternal}
        />
      ) : data ? (
        <PublisherForm
          onSubmit={onSubmit}
          onClose={onClose}
          id={id}
          data={data}
        />
      ) : (
        <div></div>
      )}
    </Modal>
  );
};

interface PublisherFormProps {
  onClose: () => void;
  onSubmit?: (id?: number) => void | Promise<void>;
  id?: number;
  data?: Server.PublisherDashboardOutput;
  isLoadingExternal?: boolean;
}

const PublisherForm = ({
  id,
  onSubmit,
  onClose,
  data,
  isLoadingExternal,
}: PublisherFormProps) => {
  const { status: createPublisherStatus } = useCreatePublisher();
  const { status: editPublisherStatus } = useEditPublisher();

  const { handleSubmit, control } = useForm<Input>({ defaultValues: data });

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    if (id) {
      editPublisher({ ...data, id }, onSubmit);
    } else {
      createPublisher(data, onSubmit);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      noValidate
      className="w-full flex flex-col justify-start items-start"
    >
      <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
        <FormTextInput
          name="name"
          control={control}
          fieldType={{
            fieldType: Server.FieldType.Text,
            fieldOptions: {},
            required: true,
            label: 'Name',
          }}
          errorMessages={{
            required: 'This field is required',
          }}
          heightClassName="h-[34px]"
          widthClassName="w-full"
          isRtl
        />
      </div>
      <div className="mt-6 w-full flex justify-end items-center space-s-2">
        <div className="w-[70px]">
          <ButtonHeadless
            bgStyle="bg-warmGray-300"
            borderStyle="border-none"
            paddingYStyle="py-1"
            action={onClose}
          >
            <div className="text-warmGray-600 text-[16px] font-medium">
              <ReturnTranslatedText text="buttons.Cancel" origintext="Cancel" />
            </div>
          </ButtonHeadless>
        </div>
        <div className="w-[70px]">
          <ButtonHeadless
            type="submit"
            borderStyle="border-none"
            paddingYStyle="py-1"
          >
            <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
              <ReturnTranslatedText text="buttons.Save" origintext="Save" />
              {(createPublisherStatus === 'loading' ||
                editPublisherStatus === 'loading' ||
                isLoadingExternal) && (
                <BiLoaderAlt className="animate-spin w-5" />
              )}
            </div>
          </ButtonHeadless>
        </div>
      </div>
    </form>
  );
};

export default PublisherModal;
